<template>
  <div>
    <b-card>
      <h4 class="mb-2">Company Settings</h4>

      <div v-if="isLoading" class="text-center py-2">
        <b-spinner label="Loading..." />
      </div>

      <b-form v-else @submit="onSubmit">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Name" label-for="company-name">
              <b-form-input id="company-name" v-model="form.name" required />
            </b-form-group>
            <b-form-group label="Email" label-for="company-email">
              <b-form-input id="company-email" v-model="form.email" type="email" />
            </b-form-group>
            <b-form-group label="Phone" label-for="company-phone">
              <b-form-input id="company-phone" v-model="form.phone" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Tax Name" label-for="company-tax-name">
              <b-form-input id="company-tax-name" v-model="form.tax_name" />
            </b-form-group>
            <b-form-group label="Tax ID" label-for="company-tax-id">
              <b-form-input id="company-tax-id" v-model="form.tax_id" />
            </b-form-group>
            <b-form-group label="Tax Value (%)" label-for="company-tax-value">
              <b-form-input
                id="company-tax-value"
                v-model="form.tax_value"
                type="number"
                step="0.01"
                min="0"
                :readonly="form.prices_include_tax"
                :disabled="form.prices_include_tax"
              />
            </b-form-group>
            <b-form-group label-for="prices-include-tax" class="mb-0">
              <b-form-checkbox
                id="prices-include-tax"
                v-model="form.prices_include_tax"
                switch
                class="mb-1"
              >
                All prices will include the tax (this will affect on new orders only).
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="align-items-center py-2">
          <b-col cols="12" md="6">
            The main purpose of the company
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-start">
              <b-form-group
                label="Sell goods"
                label-for="company-purpose-sell"
                class="mb-0 mr-3"
              >
                <b-form-checkbox
                  id="company-purpose-sell"
                  v-model="form.sellGoods"
                  switch
                  class="mb-1"
                />
              </b-form-group>
              <b-form-group
                label="Provide services"
                label-for="company-purpose-service"
                class="mb-0"
              >
                <b-form-checkbox
                  id="company-purpose-service"
                  v-model="form.provideServices"
                  switch
                  class="mb-1"
                />
              </b-form-group>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" col-span="2" class="py-2">
            <hr size="1" />
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Logo" label-for="company-picture">
              <b-form-file
                id="company-picture"
                v-model="form.picture"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
              />
              <img
                v-if="form.picture_preview"
                :src="form.picture_preview"
                alt="Company logo"
                class="my-1"
                :style="{ width: '150px', border: '1px solid #ddd' }"
              >
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Description" label-for="company-description">
              <b-form-textarea id="company-description" v-model="form.description" rows="5" max-rows="8" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" col-span="2" class="py-2">
            <hr size="1" />
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Country" label-for="company-country">
              <CountrySelect
                input-id="company-country"
                v-model="form.country_id"
              />
            </b-form-group>
            <b-form-group label="Region" label-for="company-region">
              <RegionSelect
                input-id="company-region"
                v-model="form.region_id"
                :country-id="form.country_id"
              />
            </b-form-group>
            <b-form-group label="City" label-for="company-city">
              <b-form-input id="company-city" v-model="form.city" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Address Line 1" label-for="company-address-1">
              <b-form-input id="company-address-1" v-model="form.address_line_1" />
            </b-form-group>
            <b-form-group label="Address Line 2" label-for="company-address-2">
              <b-form-input id="company-address-2" v-model="form.address_line_2" />
            </b-form-group>
            <b-form-group label="Zip Code" label-for="company-zip">
              <b-form-input id="company-zip" v-model="form.zip_code" />
            </b-form-group>
          </b-col>
        </b-row>
        
        <b-row>
          <b-col cols="12" col-span="2" class="">
            <hr size="1" />
          </b-col>
        </b-row>

        <h4 class="mt-2">Order settings</h4>

        <b-row class="pt-2 align-items-center">
          <b-col cols="12" md="6">
            Currency
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label-for="company-currency" class="mb-0">
              <v-select
                id="company-currency"
                v-model="form.currency"
                :options="currencyOptions"
                label="displayLabel"
                :clearable="false"
                placeholder="Select currency"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="pt-2 align-items-center">
          <b-col cols="12" md="6">
            Timezone
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label-for="company-timezone" class="mb-0">
              <v-select
                id="company-timezone"
                v-model="form.timezone"
                :options="timezoneOptions"
                label="displayLabel"
                :clearable="true"
                placeholder="Select timezone"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="pt-2 pb-2 align-items-center">
          <b-col cols="12" md="6">
            Time format
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label-for="company-time-format" class="mb-0">
              <v-select
                id="company-time-format"
                v-model="form.time_format"
                :options="timeFormatOptions"
                label="label"
                :reduce="(option) => option.value"
                :clearable="false"
                placeholder="Select time format"
              />
            </b-form-group>
          </b-col>
        </b-row>
        
        <b-row class="align-items-center" v-if="true || companyPurpose !== 'rent'">
          <b-col cols="12" md="6">
            Default order type 
          </b-col>
          <b-col cols="12" md="6">
            <b-row>
              <b-col cols="6">
                <b-form-group
                  label="Delivery"
                  label-for="default-order-delivery"
                  class="mb-0"
                >
                  <b-form-checkbox
                    id="default-order-delivery"
                    v-model="form.delivery"
                    switch
                    class="mb-1"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group
                  label="In store pick up"
                  label-for="default-order-pickup"
                  class="mb-0"
                >
                  <b-form-checkbox
                    id="default-order-pickup"
                    v-model="form.pickup"
                    switch
                    class="mb-1"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row class="pt-2">
          <b-col cols="12" md="6">
            Accept partial payments
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group
              label=""
              label-for="accept-partial-payments"
            >
              <b-form-checkbox
                id="accept-partial-payments"
                v-model="form.accept_partial_payments"
                switch
                class="mb-1"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="pt-2 align-items-center">
          <b-col cols="12" md="6">
            New delivery order default status
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label-for="new-delivery-order-default-status" class="mb-0">
              <v-select
                id="new-delivery-order-default-status"
                v-model="form.new_delivery_order_default_status"
                :options="selectableSaleStatuses"
                label="name"
                :reduce="saleStatusId"
                :clearable="false"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="pt-2 pb-2 align-items-center">
          <b-col cols="12" md="6">
            New pickup order default status
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label-for="new-pickup-order-default-status" class="mb-0">
              <v-select
                id="new-pickup-order-default-status"
                v-model="form.new_pickup_order_default_status"
                :options="selectableSaleStatuses"
                label="name"
                :reduce="saleStatusId"
                :clearable="false"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" col-span="2" class="">
            <hr size="1" />
          </b-col>
        </b-row>
        <b-button variant="primary" type="submit" :disabled="isSaving">
          {{ isSaving ? 'Saving...' : 'Save' }}
        </b-button>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { SALE_STATUSES } from '@/constants/enums'
import CountrySelect from '@/views/Components/Elements/CountrySelect.vue'
import RegionSelect from '@/views/Components/Elements/RegionSelect.vue'

export default {
  components: {
    vSelect,
    CountrySelect,
    RegionSelect,
  },
  computed: {
    selectableSaleStatuses() {
      return SALE_STATUSES.filter((status) => status.id !== -1)
    },
    currencyOptions() {
      return (this.$store.getters['companies/currencies'] || []).map((currency) => ({
        ...currency,
        displayLabel: `${currency.symbol} - ${currency.name} (${currency.code})`,
      }))
    },
    timezoneOptions() {
      return (this.$store.getters['companies/timezones'] || []).map((timezone) => ({
        ...timezone,
        displayLabel: `${timezone.label} (${timezone.name})`,
      }))
    },
    timeFormatOptions() {
      return [
        { value: '12h', label: '12 hours (AM / PM)' },
        { value: '24h', label: '24 hours' },
      ]
    },
  },
  methods: {
    saleStatusId(status) {
      return status.id
    },
    findCurrencyOption(currencyId) {
      if (currencyId == null) {
        return null
      }
      return this.currencyOptions.find((currency) => Number(currency.id) === Number(currencyId)) || null
    },
    findTimezoneOption(timezoneId) {
      if (timezoneId == null) {
        return null
      }
      return this.timezoneOptions.find((timezone) => Number(timezone.id) === Number(timezoneId)) || null
    },
  },
}
</script>

<script setup>
import { getCurrentInstance, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString } from '@/libs/utils'
import { applyCompanyWindowGlobals } from '@/libs/companyGlobals'

const store = useStore()
const instance = getCurrentInstance()
const isLoading = ref(true)
const isSaving = ref(false)
const companyPurpose = ref(window.companyPurpose)
const taxValueBeforeIncludeTax = ref(null)
const skipPricesIncludeTaxWatch = ref(false)

const form = reactive({
  id: null,
  name: '',
  email: '',
  phone: '',
  picture: null,
  picture_preview: null,
  description: '',
  tax_id: '',
  tax_name: '',
  tax_value: '',
  prices_include_tax: false,
  country_id: null,
  region_id: null,
  city: '',
  address_line_1: '',
  address_line_2: '',
  zip_code: '',
  currency: null,
  timezone: null,
  time_format: '12h',
  sellGoods: true,
  provideServices: false,
  pickup: false,
  delivery: true,
  accept_partial_payments: false,
  new_delivery_order_default_status: 0,
  new_pickup_order_default_status: 0,
})

const isTaxValueEmpty = (value) => {
  if (value === null || value === undefined || value === '') {
    return true
  }
  return Number(value) === 0
}

watch(
  () => form.prices_include_tax,
  (enabled) => {
    if (skipPricesIncludeTaxWatch.value) {
      return
    }

    if (enabled) {
      taxValueBeforeIncludeTax.value = form.tax_value
      form.tax_value = 0
      return
    }

    if (taxValueBeforeIncludeTax.value !== null && taxValueBeforeIncludeTax.value !== undefined) {
      form.tax_value = taxValueBeforeIncludeTax.value
      taxValueBeforeIncludeTax.value = null
    }
  },
)

watch(() => form.sellGoods, (sellGoods) => {
  if (sellGoods) {
    form.provideServices = false
  }
})

watch(() => form.provideServices, (provideServices) => {
  if (provideServices) {
    form.sellGoods = false
  }
})

watch(() => form.pickup, (pickup) => {
  if (pickup) {
    form.delivery = false
  }
})

watch(() => form.delivery, (delivery) => {
  if (delivery) {
    form.pickup = false
  }
})

const toPictureUrl = (value) => {
  if (!value) return null
  const p = String(value).trim()
  if (!p) return null
  if (/^https?:\/\//i.test(p) || p.startsWith('//')) return p
  return p.startsWith('/') ? p : `/${p}`
}

const loadData = async () => {
  isLoading.value = true
  try {
    await Promise.all([
      store.dispatch('companies/loadCountries'),
      store.dispatch('companies/loadCurrencies'),
      store.dispatch('companies/loadTimezones'),
      store.dispatch('companies/loadCompanySettings'),
    ])

    const settings = store.getters['companies/companySettings']
    const currencyList = store.getters['companies/currencies'] || []
    const findCurrencyOption = instance.proxy.findCurrencyOption
    form.id = settings.id
    form.name = settings.name || ''
    form.email = settings.email || ''
    form.phone = settings.phone || ''
    form.picture_preview = toPictureUrl(settings.picture)
    form.description = settings.description || ''
    form.tax_id = settings.tax_id || ''
    form.tax_name = settings.tax_name || ''
    const taxIsEmpty = isTaxValueEmpty(settings.tax_value)
    skipPricesIncludeTaxWatch.value = true
    form.prices_include_tax = taxIsEmpty
    form.tax_value = taxIsEmpty ? 0 : settings.tax_value
    taxValueBeforeIncludeTax.value = null
    skipPricesIncludeTaxWatch.value = false
    form.country_id = settings.country_id ?? null
    form.region_id = settings.region_id ?? null
    form.city = settings.city || ''
    form.address_line_1 = settings.address_line_1 || ''
    form.address_line_2 = settings.address_line_2 || ''
    form.zip_code = settings.zip_code || ''
    const savedCurrencyId = settings.currency_id != null
      ? Number(settings.currency_id)
      : (currencyList[0]?.id ?? null)
    form.currency = findCurrencyOption(savedCurrencyId) || findCurrencyOption(currencyList[0]?.id)
    form.timezone = instance.proxy.findTimezoneOption(settings.timezone_id)
    form.time_format = settings.time_format === '24h' ? '24h' : '12h'
    if (settings.purpose === 'service') {
      form.provideServices = true
      form.sellGoods = false
    } else {
      form.sellGoods = true
      form.provideServices = false
    }
    companyPurpose.value = settings.purpose || window.companyPurpose
    if (settings.default_order_type === 'pickup') {
      form.pickup = true
      form.delivery = false
    } else {
      form.pickup = false
      form.delivery = true
    }
    form.accept_partial_payments = Boolean(settings.accept_partial_payments)
    form.new_delivery_order_default_status = settings.new_delivery_order_default_status ?? 0
    form.new_pickup_order_default_status = settings.new_pickup_order_default_status ?? 0
  } catch (error) {
    toastFailure('Failed to load company settings.')
  } finally {
    isLoading.value = false
  }
}

const onSubmit = async (event) => {
  if (event && event.preventDefault) {
    event.preventDefault()
  }
  if (!form.name || !form.country_id) {
    toastFailure('Name and country are required.')
    return
  }

  if (!form.currency || !form.currency.id) {
    toastFailure('Please select a currency.')
    return
  }

  if (!form.sellGoods && !form.provideServices) {
    toastFailure('Please choose the main purpose of the company: sell goods or provide services.')
    return
  }

  if (!form.pickup && !form.delivery) {
    toastFailure('Please choose a default order type: delivery or in-store pick up.')
    return
  }

  isSaving.value = true
  try {
    const formData = new FormData()
    formData.append('name', form.name)
    formData.append('purpose', form.provideServices ? 'service' : 'sell')
    formData.append('email', form.email)
    formData.append('phone', form.phone)
    formData.append('description', form.description || '')
    formData.append('tax_id', form.tax_id || '')
    formData.append('tax_name', form.tax_name || '')
    const taxValueToSave = form.prices_include_tax
      ? '0'
      : (form.tax_value === '' ? '0' : String(form.tax_value))
    formData.append('tax_value', taxValueToSave)
    formData.append('country_id', String(form.country_id))
    formData.append('currency_id', String(form.currency.id))
    if (form.timezone && form.timezone.id) {
      formData.append('timezone_id', String(form.timezone.id))
    } else {
      formData.append('timezone_id', '')
    }
    formData.append('time_format', form.time_format === '24h' ? '24h' : '12h')
    formData.append('region_id', form.region_id != null && form.region_id !== '' ? String(form.region_id) : '')
    formData.append('city', form.city || '')
    formData.append('address_line_1', form.address_line_1 || '')
    formData.append('address_line_2', form.address_line_2 || '')
    formData.append('zip_code', form.zip_code || '')
    formData.append('default_order_type', form.pickup ? 'pickup' : 'delivery')
    formData.append('accept_partial_payments', form.accept_partial_payments ? '1' : '0')
    formData.append(
      'new_delivery_order_default_status',
      String(form.new_delivery_order_default_status ?? 0),
    )
    formData.append(
      'new_pickup_order_default_status',
      String(form.new_pickup_order_default_status ?? 0),
    )
    if (form.picture) {
      formData.append('picture', form.picture)
    }

    const updated = await store.dispatch('companies/updateCompanySettings', { formData })
    form.picture = null
    form.picture_preview = toPictureUrl(updated.picture)
    form.currency = instance.proxy.findCurrencyOption(updated.currency_id) || form.currency
    form.timezone = instance.proxy.findTimezoneOption(updated.timezone_id)
    form.time_format = updated.time_format === '24h' ? '24h' : '12h'
    applyCompanyWindowGlobals(updated)
    companyPurpose.value = updated.purpose || companyPurpose.value
    if (updated.purpose === 'service') {
      form.provideServices = true
      form.sellGoods = false
    } else {
      form.sellGoods = true
      form.provideServices = false
    }
    toastSuccess('Company settings saved successfully.')
  } catch (error) {
    toastFailure(backendErrorString(error))
  } finally {
    isSaving.value = false
  }
}

onMounted(loadData)
</script>
